import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { withoutEmbedWorkflowParams } from '../utils/embedWorkflow';
import { useEmbeddedWorkflowActive } from './useEmbeddedWorkflowActive';

/**
 * Back handler for a page that can hand its body to `EmbeddedWorkflowView`.
 *
 * Back closes the embed first: the workflow is entered by replacing the host
 * page's history entry, so dropping the params in place returns to the page's
 * own content without a navigation, and leaves Back pointing at wherever the
 * page was opened from.
 *
 * Once the embed has been open, history is no longer a way out: every
 * navigation MSR2 makes inside the iframe adds an entry to the joint session
 * history, so `-1` mostly rewinds the iframe and the page appears stuck. The
 * page leaves by path instead.
 *
 * @param fallbackPath Where to go when history cannot be stepped back through.
 */
export const useEmbeddedWorkflowBack = (fallbackPath: string) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [, setSearchParams] = useSearchParams();
  const isEmbeddingWorkflow = useEmbeddedWorkflowActive();

  // Written from an effect, never during render: a render React discards must
  // not be able to strand this at `true`.
  const hasOpenedEmbed = useRef(false);

  useEffect(() => {
    if (isEmbeddingWorkflow) {
      hasOpenedEmbed.current = true;
    }
  }, [isEmbeddingWorkflow]);

  return () => {
    if (isEmbeddingWorkflow) {
      setSearchParams(withoutEmbedWorkflowParams, { replace: true, state });

      return;
    }

    const canGoBack =
      !hasOpenedEmbed.current &&
      typeof window.history.state?.idx === 'number' &&
      window.history.state.idx > 0;

    if (canGoBack) {
      navigate(-1);

      return;
    }

    // Replaces this page's entry rather than stacking another on top of the
    // iframe's, so leaving does not add one more entry to walk back through.
    // Entries MSR2 pushed inside the iframe sit below it and are untouched,
    // which is why `canGoBack` refuses `-1` once the embed has been open.
    navigate(fallbackPath, { replace: true });
  };
};
